/**
 * Read Slide Tool - Schema Definitions
 * 
 * Zod schemas for reading slides from a PowerPoint presentation
 * with configurable detail levels and filtering options.
 */

import { z } from "zod";
import type { SlideInfoEntry } from "../../../../globals/llm/reminder/types";

// ═══════════════════════════════════════════════════════════════════════════
// INPUT SCHEMAS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Detail level for slide content retrieval
 * - layout: Fast structural scan (text, positions, basic structure)
 * - complete: Full details including provenance and raw XML
 */
export const DetailLevelSchema = z.enum(["layout", "complete"]);

/**
 * Single slide request definition
 */
export const SlideRequestSchema = z.object({
    /** Unique identifier for the slide (e.g., "slide_abc123"). Used for slide identification. */
    slideId: z.string()
        .optional()
        .describe('Unique identifier for the slide (e.g., "slide_abc123"). Used for slide identification.'),
    
    /** One-based index of the slide in the presentation (e.g., 1, 5, 12). Used for ordering and reordering slides. */
    slideIndex: z.number()
        .int()
        .positive()
        .optional()
        .describe('One-based index of the slide in the presentation (e.g., 1, 5, 12). Used for ordering and reordering slides.'),
    
    /** Level of detail to return: "layout" for basic structure or "complete" for full details including all properties. */
    detailLevel: DetailLevelSchema
        .describe('Level of detail to return: "layout" for basic structure or "complete" for full details including all properties.'),
    
    /** If true (default), include a base64-encoded image of the slide for visual analysis. Set to false to skip image capture and reduce token usage. */
    includeImage: z.boolean()
        .optional()
        .default(true)
        .describe('If true (default), include a base64-encoded image of the slide for visual analysis. Set to false to skip image capture and reduce token usage.'),
    
    /** If true, include speaker notes for this slide. */
    includeNotes: z.boolean()
        .optional()
        .describe('If true, include speaker notes for this slide.'),

    /** If true (default), read all shapes on the slide. If false and shapeIds is provided, only read specified shapes. */
    allShapes: z.boolean()
        .optional()
        .default(true)
        .describe('If true (default), read all shapes on the slide. If false and shapeIds is provided, only read specified shapes.'),
    
    /** Array of specific shape IDs to read. Only used when allShapes is false. */
    shapeIds: z.array(z.string())
        .optional()
        .describe('Array of specific shape IDs to read. Only used when allShapes is false.'),
}).refine(
    (data) => data.slideId !== undefined || data.slideIndex !== undefined,
    { message: "Either slideId or slideIndex must be provided" }
);

/**
 * Tool input parameters
 */
export const ReadSlideParamsSchema = z.object({
    uiDescription: z.string()
        .describe('Brief description (max ~15 words) of what this tool call is attempting.'),
    slides: z.array(SlideRequestSchema)
        .min(1, "At least one slide must be requested")
        .describe('Array of slide requests to read. Each request specifies which slide to read and what level of detail to return.'),
});

// ═══════════════════════════════════════════════════════════════════════════
// OUTPUT TYPES (not Zod schemas - response types)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Position object (x, y, width, height in points)
 */
export interface Position {
    x: number;
    y: number;
    width: number;
    height: number;
}

/**
 * Text frame properties (margins and text layout settings)
 */
export interface TextFrameContent {
    marginLeft?: number;
    marginTop?: number;
    marginRight?: number;
    marginBottom?: number;
    wordWrap?: boolean;
    verticalAnchor?: string;
    autofit?: string;
    rotation?: number;
}

/**
 * Paragraph content with text runs
 */
export interface ParagraphContent {
    text: string;
    level: number;
    runs?: Array<{
        text: string;
        font?: {
            size_pt?: number;
            bold?: boolean;
            italic?: boolean;
            underline?: string;
            color?: string;
            font_name?: string;
        };
    }>;
    alignment?: string;
    bullet_char?: string;
}

/**
 * Fill content (solid, gradient, pattern, or none)
 */
export interface FillContent {
    type: 'solid' | 'gradient' | 'pattern' | 'none';
    color?: string;
    stops?: Array<{ position: number; color: string }>;
    preset?: string;
}

/**
 * Outline/line content
 */
export interface OutlineContent {
    width?: number;
    color?: string;
    visible?: boolean;
    cap?: string;
    dash?: string;
}

/**
 * Table cell content
 */
export interface TableCellContent {
    row: number;
    column: number;
    row_span?: number;
    col_span?: number;
    text?: string;
    fill_color?: string;
    borders?: Record<string, { width: number }>;
    vertical_align?: string;
    margins?: Record<string, number>;
}

/**
 * Table content
 */
export interface TableContent {
    rows: number;
    columns: number;
    column_widths?: number[];
    cells: TableCellContent[];
}

/**
 * Geometry content
 */
export interface GeometryContent {
    type: string;
    adjustments?: Record<string, number>;
}

/**
 * Effects content (shadows, glow, etc.)
 */
export interface EffectsContent {
    shadow?: {
        blur_radius?: number;
        distance?: number;
        direction?: number;
        color?: string;
    };
    reflection?: boolean;
    glow?: {
        radius?: number;
        color?: string;
    };
}

/**
 * Extension content (custom XML data)
 */
export interface ExtensionContent {
    uri: string;
    content: string;
}

/**
 * Provenance content (for debugging/tracing)
 */
export interface ProvenanceContent {
    part: string;
    xpath: string;
    raw_xml: string;
}

/**
 * Shape content returned from reading
 * Properties vary by detail level
 * 
 * TEXT HANDLING: A shape has EITHER paragraphs OR extractedChartText, not both:
 * - paragraphs: Structured text with levels/runs (text boxes, bullet lists)
 * - extractedChartText: Flat text extracted from charts/images (axis labels, legend, data labels, alt text)
 *                       NOT structured paragraphs - just extracted component text
 */
export interface ShapeContent {
    // Core properties (all detail levels)
    shapeId: string;
    name?: string;
    type: string;
    zIndex: number;
    position: Position;
    textBounds?: Position;      // Calculated inner text area
    textFrame?: TextFrameContent;
    
    // Text content - EITHER paragraphs OR extractedChartText, not both
    paragraphs?: ParagraphContent[];           // Structured text (text boxes, bullets)
    extractedChartText?: string;               // Flat text from charts/images (axis labels, legend, alt text)
    
    placeholder?: { type: string; idx?: number; size?: string; };
    description?: string;       // Image alt text
    title?: string;
    tags?: Record<string, string>;  // Custom tags
    childShapes?: ShapeContent[];
    tableInfo?: TableContent;
    
    // Styled/complete properties
    fill?: FillContent;
    outline?: OutlineContent;
    from?: string;              // Source of inherited properties
    inherited?: boolean;
    
    // Complete-only properties
    hidden?: boolean;
    rotation?: number;
    geometry?: GeometryContent;
    effects?: EffectsContent;
    extensions?: ExtensionContent[];
    provenance?: ProvenanceContent;
    
    // Carousel-specific metadata (unified entries)
    // Available at complete detail level
    carouselMetadata?: {
        /** Unified metadata entries for this shape */
        entries?: Array<{
            type: string;
            sourceContextId: string;
            mapsTo?: Array<{
                text?: string[];
                row?: number;
                col?: number;
            }>;
            createdAt?: string;
            updatedAt?: string;
        }>;
    };
}

/**
 * Slide content returned from reading
 */
export interface SlideContent {
    slideId: string;             // e.g., "slide_abc123"
    slideIndex: number;          // One-based position (1, 2, 3...)
    title?: string;              // Slide title if present
    layout?: string;             // Layout reference path from xml_parser
    layoutId?: string;           // Office.js numeric ID (e.g., "layout_2147483951")
    layoutName?: string;         // Human-readable layout name
    masterTemplateId?: string;   // Office.js numeric ID (e.g., "master_2147483952")
    masterTemplateName?: string; // Human-readable master name
    shapes: ShapeContent[];
    notes?: string;              // Speaker notes if requested
    /**
     * @deprecated Do NOT use! Images should NOT be in tool results - they bloat context window.
     * Images are stored in contextResolver and referenced via _imageIds in ReadSlideResponse.
     * This field exists only for backwards compatibility and should not be populated.
     */
    image?: string;
}

/**
 * Tool response
 * 
 * Returns TEXT format to the LLM (human-readable, token-efficient).
 * Full JSON is stored in context resolver for sub-agents to access.
 * 
 * This separation is intentional:
 * - LLM sees: Human-readable text (easy to understand slide content)
 * - Sub-agents get: Full JSON via contextIds (shapeId, positions, etc.)
 */
export interface ReadSlideResponse {
    /**
     * Text-formatted slide content for LLM comprehension.
     * Each string is a human-readable representation of a slide's content.
     * 
     * Example:
     * ```
     * === SLIDE 7 ===
     * Layout: NAV - Table of Contents
     * 
     * --- SHAPES (15 total) ---
     * 
     * [1] TABLE: Table 1 (ID: 2, from: slide)
     *     Position: x=52.1, y=268.7, w=614.3, h=175.6
     *     Table: 6 rows x 3 columns
     * ```
     */
    slides: string[];
    
    /** 
     * Context IDs for each slide - pass these to sub-agents.
     * Sub-agents resolve these to get full JSON with shapeId, positions, etc.
     * Example: ["slide_318", "slide_319"]
     */
    contextIds: string[];
    
    /**
     * Image IDs for automatic injection in next user message.
     * Convention used by extractImageIds() in messageBuilder.
     * Images are stored in contextResolver and referenced by these IDs.
     */
    _imageIds?: string[];

    /**
     * Slide tracking entries for pruning (internal, stripped before LLM).
     */
    _slideInfos?: SlideInfoEntry[];
    
    /** Note explaining how to use the returned context IDs */
    _note?: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// TYPE EXPORTS
// ═══════════════════════════════════════════════════════════════════════════

export type DetailLevel = z.infer<typeof DetailLevelSchema>;
export type SlideRequest = z.infer<typeof SlideRequestSchema>;
export type ReadSlideParams = z.infer<typeof ReadSlideParamsSchema>;
